import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import routesConfig from 'constants/routesConfig.json';

// assets
import {
  Charts,
  Communication,
  General,
  Layout,
  MediaDevices,
  Users,
} from 'icons/untitledui';
import LogoText from 'shared/assets/logoText.svg';

// components
import DemoMessage from 'sidePanel/components/DemoMessage';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import Logout from 'auth/components/Logout';
import SettingsTab from 'sidePanel/components/SettingsTab';
import PanelNav from 'sidePanel/components/PanelNav';
import Tooltip from 'sidePanel/components/Tooltip';
import { TopBar } from 'topBar/components/TopBar';

// context
import { useUser } from 'shared/hooks/api/user';
import useCanAccessConversations from 'eCoach/hooks/useCanAccessConversations';
import useWindowSize from 'shared/hooks/useWindowResize';

// styles
import breakpoints from 'theme/atoms/breakpoints';
import * as PanelNavStyles from 'sidePanel/styles/PanelNav.style';
import * as Styled from 'sidePanel/styles/SidePanel.style';

const SIDE_PANEL_STORAGE_KEY = 'nurau_side_panel_open';

export default function SidePanel(): React.ReactElement {
  const { t } = useTranslation('sidePanel');
  const { data: user } = useUser();
  const posthog = usePostHog();
  const history = useHistory();
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const windowSize = useWindowSize();
  const windowWidth = windowSize[0];

  const searchParams = new URLSearchParams(location.search);
  const showSidePanel = searchParams.get('showpanel');
  const isTourActive = searchParams.get('tour') === 'true';

  const [pageSelection, setPageSelection] = useState('');
  const [sidePanelOpen, setSidePanelOpen] = useState(() => {
    if (showSidePanel !== null) return showSidePanel === 'true';
    const stored = localStorage.getItem(SIDE_PANEL_STORAGE_KEY);
    return stored !== null ? stored === 'true' : false;
  });
  const prevPathnameRef = useRef(pathname);
  const [showCollapseTooltip, setShowCollapseTooltip] = useState(false);

  const canAccessShiftNotes = posthog.isFeatureEnabled('shift_notes');
  const canAccessTeamPage =
    !!user?.member?.site &&
    canAccessShiftNotes &&
    posthog.isFeatureEnabled('team_page');
  const canAccessConversations = useCanAccessConversations();
  const desktopBreakpoint = parseInt(breakpoints.laptop, 10);

  useEffect(() => {
    if (windowWidth > desktopBreakpoint) {
      const stored = localStorage.getItem(SIDE_PANEL_STORAGE_KEY);
      setSidePanelOpen(stored !== null ? stored === 'true' : false);
    }
  }, [windowWidth, desktopBreakpoint]);

  useEffect(() => {
    if (pathname === routesConfig.ABOUT_PAGE) setPageSelection('about');
    if (pathname === routesConfig.FAQ_PAGE) setPageSelection('faq');
    if (pathname === routesConfig.INSIGHTS_PAGE) setPageSelection('insights');
    if (
      pathname === routesConfig.EVENTS_PAGE ||
      pathname.startsWith(`${routesConfig.EVENT_PAGE}/`)
    )
      setPageSelection('events');
    if (pathname.startsWith(`${routesConfig.SHIFT_NOTES_PAGE}/`))
      setPageSelection('shift notes');
    if (pathname.includes(`${routesConfig.DOCUMENT_PAGE}/`))
      setPageSelection('document');
    if (pathname.includes(`${routesConfig.CONVO_PAGE}/`))
      setPageSelection('conversation');
    if (pathname === routesConfig.DEMO_SETTINGS_PAGE)
      setPageSelection('demo settings');
    if (pathname === routesConfig.TEAM_PAGE || pathname.includes('/team/'))
      setPageSelection('team');

    // Handle side panel state based on URL parameters
    // On desktop during tours, keep panel open regardless of showpanel parameter
    if (
      showSidePanel === 'false' &&
      !(isTourActive && windowWidth > desktopBreakpoint)
    ) {
      setSidePanelOpen(false);
    }

    if (showSidePanel === 'true') setSidePanelOpen(true);

    // Remove showpanel parameter when pathname changes on mobile (after navigation)
    // This ensures the URL is clean after navigation without interfering with manual panel opening
    if (
      windowWidth <= desktopBreakpoint &&
      pathname !== prevPathnameRef.current &&
      location.search.includes('showpanel')
    ) {
      const urlParams = new URLSearchParams(location.search);
      if (urlParams.has('showpanel')) {
        urlParams.delete('showpanel');
        const newSearch = urlParams.toString();
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        });
      }
    }
    prevPathnameRef.current = pathname;
  }, [
    showSidePanel,
    pathname,
    isTourActive,
    windowWidth,
    desktopBreakpoint,
    history,
    location,
  ]);

  const setPage = useCallback(
    (page: string) => {
      setPageSelection(page);
      if (windowWidth <= desktopBreakpoint) {
        setSidePanelOpen(false);
      }
    },
    [desktopBreakpoint, windowWidth]
  );

  const resetApp = useCallback(() => {
    history.push(routesConfig.ROOT_PAGE);
    setPageSelection('');
    if (windowWidth <= desktopBreakpoint) setSidePanelOpen(false);
  }, [history, windowWidth, desktopBreakpoint]);

  const toggleSidePanel = useCallback(() => {
    // Prevent closing side panel on desktop during tours
    if (isTourActive && windowWidth > desktopBreakpoint && sidePanelOpen) {
      return;
    }
    const newState = !sidePanelOpen;
    setSidePanelOpen(newState);
    // Persist preference for desktop
    if (windowWidth > desktopBreakpoint) {
      localStorage.setItem(SIDE_PANEL_STORAGE_KEY, String(newState));
    }
    // Update URL parameter when toggling on mobile
    if (windowWidth <= desktopBreakpoint) {
      const urlParams = new URLSearchParams(location.search);
      if (newState) {
        urlParams.set('showpanel', 'true');
      } else {
        urlParams.delete('showpanel');
      }
      const newSearch = urlParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, [
    isTourActive,
    windowWidth,
    desktopBreakpoint,
    sidePanelOpen,
    history,
    location,
  ]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        windowWidth <= desktopBreakpoint &&
        sidePanelOpen &&
        e.target === e.currentTarget
      ) {
        setSidePanelOpen(false);
        // Remove showpanel parameter from URL when closing
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.has('showpanel')) {
          urlParams.delete('showpanel');
          const newSearch = urlParams.toString();
          history.replace({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
          });
        }
      }
    },
    [windowWidth, sidePanelOpen, desktopBreakpoint, history, location]
  );

  const openAbout = useCallback(() => {
    history.push(routesConfig.ABOUT_PAGE);
    setPage('about');
  }, [history, setPage]);

  const openConversations = useCallback(() => {
    history.push(`${routesConfig.ALL_CONVOS_PAGE}`);
    setPage('all conversations');
  }, [history, setPage]);

  const openInsights = useCallback(() => {
    history.push(`${routesConfig.INSIGHTS_PAGE}`);
    setPage('insights');
  }, [history, setPage]);

  const openEvents = useCallback(() => {
    history.push(routesConfig.EVENTS_PAGE);
    setPage('events');
  }, [history, setPage]);

  const openTeam = useCallback(() => {
    history.push(routesConfig.TEAM_PAGE);
    setPage('team');
  }, [history, setPage]);

  const openDemoSettings = useCallback(() => {
    history.push(`${routesConfig.DEMO_SETTINGS_PAGE}`);
    setPage('demo settings');
  }, [history, setPage]);

  const openSettings = useCallback(() => {
    history.push(`${routesConfig.SETTINGS_PAGE}`);
    setPage('settings');
  }, [history, setPage]);

  return (
    <ErrorBoundary>
      <TopBar
        onBackClick={() => setSidePanelOpen(true)}
        showBackButton={!sidePanelOpen && windowWidth <= desktopBreakpoint}
        backButtonAriaLabel={t('Menu')}
      />

      <Styled.Container expanded={sidePanelOpen} onClick={handleOverlayClick}>
        <Styled.InnerContainer
          expanded={sidePanelOpen}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          <Styled.Navigation>
            <Styled.PanelHeader expanded={sidePanelOpen}>
              {sidePanelOpen && (
                <Styled.PanelHeaderLogo
                  onClick={resetApp}
                  style={{ width: 'unset' }}
                >
                  <img src={LogoText} />
                </Styled.PanelHeaderLogo>
              )}
              <Tooltip
                content={
                  sidePanelOpen ? t('collapseSidePanel') : t('expandSidePanel')
                }
                visible={showCollapseTooltip}
              >
                <PanelNavStyles.Container
                  onClick={toggleSidePanel}
                  onPointerEnter={(e: React.PointerEvent) => {
                    if (e.pointerType !== 'mouse') return;
                    setShowCollapseTooltip(true);
                  }}
                  onPointerLeave={() => setShowCollapseTooltip(false)}
                  style={{ padding: 'unset' }}
                >
                  <Layout.LayoutLeft />
                </PanelNavStyles.Container>
              </Tooltip>
            </Styled.PanelHeader>
            {sidePanelOpen && <DemoMessage />}
            {canAccessShiftNotes && (
              <PanelNav
                action={openEvents}
                icon={() => <MediaDevices.Microphone01 />}
                isActive={pageSelection === 'events'}
                expanded={sidePanelOpen}
                label={t('Events')}
              />
            )}
            {canAccessTeamPage && (
              <PanelNav
                action={openTeam}
                icon={() => <Users.Users01 />}
                isActive={pageSelection === 'team'}
                expanded={sidePanelOpen}
                label={t('Team')}
              />
            )}
            {canAccessShiftNotes && (user.is_team_lead || user.is_demo) && (
              <PanelNav
                action={openInsights}
                demoTour="demo-tour-step-8"
                icon={() => <Charts.BarChartSquare01 />}
                isActive={pageSelection === 'insights'}
                expanded={sidePanelOpen}
                label={t('Insights')}
              />
            )}
            {canAccessConversations && (
              <PanelNav
                action={openConversations}
                icon={() => <Communication.MessageChatSquare />}
                demoTour="demo-tour-step-5 onboarding-tour-step-6"
                isActive={['all conversations', ''].includes(pageSelection)}
                expanded={sidePanelOpen}
                label={t('Conversations')}
              />
            )}
          </Styled.Navigation>

          <Styled.Navigation>
            {user.is_internal && user.is_demo && (
              <PanelNav
                action={openDemoSettings}
                icon={() => <General.Settings01 />}
                expanded={sidePanelOpen}
                label={t('Demo settings')}
              />
            )}

            <SettingsTab
              expanded={sidePanelOpen}
              onOpenSettings={openSettings}
            />
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between',
              }}
            >
              <Logout expanded={sidePanelOpen} />
              {sidePanelOpen && (
                <PanelNav
                  action={openAbout}
                  icon={() => <General.HelpCircle />}
                />
              )}
            </div>
          </Styled.Navigation>
        </Styled.InnerContainer>
      </Styled.Container>
    </ErrorBoundary>
  );
}
